import { memo, useEffect, useRef, useCallback } from 'react';
import { shallowEqual } from 'react-redux';
import { useOutletContext } from 'react-router-dom';

import { REQUESTS_TABS } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { clanIsFull } from '~/helpers/clan';
import { showInviteAcceptDialog } from '~/helpers/dialogs';
import useMount from '~/hooks/useMount';
import { hasPermission, PERMISSIONS } from '~/roles';
import { useAppDispatch, useAppSelector } from '~/store';
import { isClansFetchingSelector } from '~/store/selectors/clanSelectors';
import { currentAccountSelector } from '~/store/selectors/currentAccountSelector';
import { fetchClan } from '~/Actions/ActionClanProfile';
import { actionsInvites, declineInvite, fetchInvites } from '~/Actions/ActionInvites';

import headersAccountInvites from '~/Components/Requests/Invites/headers.account.settings.invites';
import headersClanInvites from '~/Components/Requests/Invites/headers.clan.settings.invites';
import Entries from '~/UIKit/Entries/Entries';
import StepCSSTransitionGroup from '~/UIKit/StepCSSTransitionGroup/StepCSSTransitionGroup';

import type { IAccountInvite } from '~/Actions/ActionInvites';
import type { RootState } from '~/store';

const stateSelector = (state: RootState) => {
  const clanId = state.currentAccount.clanId;
  const currentClan = clanId ? state.clans.items[clanId] : undefined;
  const clanLoaded = clanId ? !!state.clans.items[clanId] : false;

  return {
    clan: currentClan,
    isCurrentClanFull: !!currentClan && clanIsFull(currentClan),
    clanLoaded,
    invites: state.invites,
  };
};

const RequestsInvites = () => {
  const { animationLevelNested } = useOutletContext<{ animationLevelNested: number }>();

  const { clanLoaded, invites } = useAppSelector(stateSelector, shallowEqual);
  const isClansFetching = useAppSelector(isClansFetchingSelector);
  const currentAccount = useAppSelector(currentAccountSelector);
  const dispatch = useAppDispatch();

  useMount(() => {
    const canHandleInvites = hasPermission(currentAccount.roleName, PERMISSIONS.HANDLE_INVITES);

    if (canHandleInvites) {
      dwhExport.push(DWH_EVENTS.REQUESTS.CLAN.VIEW_INVITES);
    } else {
      dwhExport.push(DWH_EVENTS.REQUESTS.PLAYER.VIEW_INVITES);
    }

    if (currentAccount.clanId && !clanLoaded && !isClansFetching) {
      void dispatch(fetchClan(currentAccount.clanId));
    }
  });

  const activeInvitesCount = useRef(currentAccount.activeInvitesCount);

  useEffect(() => {
    if (
      !currentAccount.clanId &&
      activeInvitesCount.current !== currentAccount.activeInvitesCount &&
      !invites.isFetching
    ) {
      activeInvitesCount.current = currentAccount.activeInvitesCount;
      void dispatch(fetchInvites());
    }
  }, [currentAccount.activeInvitesCount, currentAccount.clanId, dispatch, invites.isFetching]);

  const fetchEntriesByCurrentState = useCallback(
    (withGlobalSpinner: boolean) => {
      void dispatch(fetchInvites(withGlobalSpinner));
    },
    [dispatch],
  );

  const fetchEntriesByPage = useCallback(
    (page: number) => {
      dispatch(actionsInvites.changePage(page));
      void dispatch(fetchInvites());
    },
    [dispatch],
  );

  const onAcceptedEntry = useCallback(
    (invite: IAccountInvite) => {
      dispatch(showInviteAcceptDialog(invite));
    },
    [dispatch],
  );

  const onDeclinedEntry = useCallback(
    (invite: IAccountInvite) => {
      void dispatch(declineInvite(invite));
    },
    [dispatch],
  );

  const onReloadClick = useCallback(() => {
    void dispatch(fetchInvites());
  }, [dispatch]);

  const onSortClick = useCallback(
    (order: string, isAsc: boolean) => {
      dispatch(actionsInvites.changeOrder(order, isAsc));
      void dispatch(fetchInvites());
    },
    [dispatch],
  );

  if (currentAccount.clanId) {
    return (
      <StepCSSTransitionGroup level={animationLevelNested}>
        <Entries
          isOperational={false}
          entries={invites}
          entryType={REQUESTS_TABS.INVITES}
          headers={headersAccountInvites}
          fetchEntriesByCurrentState={fetchEntriesByCurrentState}
          fetchEntriesByPage={fetchEntriesByPage}
          onReloadClick={onReloadClick}
          onSortClick={onSortClick}
        />
      </StepCSSTransitionGroup>
    );
  }

  return (
    <StepCSSTransitionGroup level={animationLevelNested}>
      <Entries
        isOperational={true}
        entries={invites}
        entryType={REQUESTS_TABS.INVITES}
        headers={headersClanInvites}
        fetchEntriesByCurrentState={fetchEntriesByCurrentState}
        fetchEntriesByPage={fetchEntriesByPage}
        onAcceptedEntry={onAcceptedEntry}
        onDeclinedEntry={onDeclinedEntry}
        onReloadClick={onReloadClick}
        onSortClick={onSortClick}
      />
    </StepCSSTransitionGroup>
  );
};

export default memo(RequestsInvites);
